<script setup lang="ts">

const breadcrumbs = [
  { name: 'Home', item: '/' },
  { name: 'Features', item: '/features' },
  { name: 'Choice Pages', item: '/features/choice-pages' }
]

useSchemaOrg([
  defineSoftwareApp({
    '@type': 'SoftwareApplication',
    '@id': 'https://geniuslink.com/choice-pages#softwareapplication',
    name: 'Geniuslink Choice Pages',
    url: 'https://geniuslink.com/choice-pages',
    description: 'Geniuslink Choice Pages create beautiful landing pages that let shoppers choose where to buy, helping creators diversify income and double affiliate earnings through comparison shopping.',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Affiliate Landing Page Builder',
    operatingSystem: 'Web',
    publisher: {
      '@type': 'Organization',
      '@id': 'https://geniuslink.com/#organization',
      name: 'Geniuslink',
      url: 'https://geniuslink.com',
      logo: 'https://geniuslink.com/img/logo.svg'
    },
    featureList: [
      'Build landing pages in seconds',
      'Automatic product suggestions from multiple retailers',
      'Custom branding with logo support',
      'Built-in FTC and Amazon compliance disclosures',
      'Amazon localization integration',
      'Multi-retailer affiliate support',
      'Customizable themes and styling',
      'Auto-sizing image cropping tool',
      'Sortable store buttons',
      'Works with Skimlinks and Sovrn Commerce'
    ]
  }),
  defineBreadcrumb({
    itemListElement: breadcrumbs
  })
])

useHead({
  title: 'Genius Link Choice Pages',
  meta: [{ name: 'description', content: 'Genius Link Choice Pages' }]
})
</script>

<template>
  <div>
    <div class="splash-area splash-area-choice">
      <div class="container">
        <div class="row breadcrumb-area">
          <div class="col-lg-8 col-md-10">
            <ol class="breadcrumb">
              <li><NuxtLink to="/">Home</NuxtLink></li>
              <li><NuxtLink to="/features/">Features</NuxtLink></li>
              <li class="active">Choice Pages</li>
            </ol>
          </div>
        </div>

        <div class="row splash-content splash-content-screenshot">
          <div class="col-lg-offset-1 col-lg-5 col-sm-6">
            <h1><span class="lead-in">Featuring:</span> <span style="white-space:nowrap">Choice Pages</span></h1>
            <p>
              Maximize revenue by giving your shoppers freedom of choice, without cluttering your site or social media. <br /> Included with every Geniuslink account.
            </p>
            <a data-fancybox="video-gallery" data-src="https://www.youtube.com/watch?v=TwZ5XrhcqEk" class="btn-splash-minor" style="display: inline-block">&#9658; Watch</a>&nbsp;
            <NuxtLink to="/pricing" class="btn-splash-minor" style="display: inline-block">Try it free</NuxtLink>
            <br><br>
          </div>
          <div class="col-lg-6 col-sm-6">
            <div class="hero-screenshot-area">
              <a target="_blank" href="https://geni.us/efnrxHy" aria-label="View live Choice Page example"><img class='img-responsive hero-screenshot' src="/img/hero-choice-screenshot-l.webp" alt="Geniuslink Choice Pages" /></a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="full-band">
      <NuxtLink class="end-tab prominent" to="/case-study/armando-ferreira/" aria-label="Read Armando Ferreira's Choice Pages case study"><span class="vsprite icon-case-study"></span> See the case study</NuxtLink>
      <div class="big-quote">
        <NuxtLink to="/case-study/armando-ferreira/" aria-label="Read Armando Ferreira's case study"><img src="/img/quote-armando.webp" alt="Armando Ferreira"></NuxtLink>
        <p>"Ever since using Choice Pages, my affiliate income has skyrocketed by over 2.6x. There is no way I would not do a Choice Page."</p>
        <p class="big-quote-by">Armando Ferreira, <a href="https://www.youtube.com/channel/UCXzySgo3V9KysSfELFLMAeA" target="_blank" rel="noopener noreferrer">Mondo Bytes</a></p>
      </div>
    </div>

    <div class="container">
      <h2 class="statement"><span class="lead-in">Beautiful choices.<br></span>Built in seconds.</h2>
      <br>

      <div class="row">
        <div class="col-md-4">
          <p>
            Most of the content in a Choice Page is set for you automatically, based on the first Amazon link you add, or by typing a product name. Build a gorgeous landing page in seconds. No coding required.
          </p>
          <p><span class="diagram-dot dd-inline"></span> See the dots to learn more.</p>
        </div>
        <div class="col-md-8">
          <div class="choice-diagram-area">
            <div class="diagram-dot dd-logo helpdot" data-aos="fade-up" data-aos-delay="0"
                 data-tipso-title="Optional page logo"
                 data-tipso-content="Place your own logo at the top of the page to reinforce your brand and help your audience feel right at home."
            ></div>
            <div class="diagram-dot dd-image helpdot" data-aos="fade-up" data-aos-delay="100"
                 data-tipso-title="Automatic or custom images"
                 data-tipso-content="We will automatically set the image for you when you link to an Amazon product. Or, upload a custom image by URL or file."
            ></div>
            <div class="diagram-dot dd-title helpdot" data-aos="fade-up" data-aos-delay="200"
                 data-tipso-title="Automatic titles you can customize"
                 data-tipso-content="Your title will be set automatically when you link to an Amazon product, but you can edit or replace it."
            ></div>
            <div class="diagram-dot dd-cta helpdot" data-aos="fade-up" data-aos-delay="300"
                 data-tipso-title="Call-to-action"
                 data-tipso-content="Use our default call-to-action text or set your own."
            ></div>
            <div class="diagram-dot dd-choices helpdot" data-aos="fade-up" data-aos-delay="400"
                 data-tipso-title="Just click the other links you want to add"
                 data-tipso-content="Once you add an Amazon link or search for a product name, a list of matching products in other stores will show. Just choose from the list of matching products or manually add URLs directly. Logos for most stores are set automatically, but you can upload your own logos, too."
            ></div>
            <div class="diagram-dot dd-disclosures helpdot" data-aos="fade-up" data-aos-delay="500"
                 data-tipso-title="Compliance without clutter"
                 data-tipso-content="By default, each Choice Page includes relevant affiliate disclosures required by Amazon and the FCC. Say goodbye to adding disclosure text inside your creative content, and feel free to safely share your Choice Page links over email."
            ></div>
            <img src="/img/choice-diagram-image.webp" class="img-responsive" width="637" height="740" alt="Choice Pages diagram">
          </div>
        </div>
      </div>

      <h2 class="statement"><span class="lead-in">The "Choice Effect"<br></span>Multiplying your revenue.</h2>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic">
            <div class="col-md-3  col-md-offset-1">
              <div class="single-topic-art">
                <img class="img-responsive" src="/img/feature-block-choice-boost.svg" alt="Unlock your full earning potential">
              </div>
            </div>
            <div class="col-md-6">
              <h4>Unlock your full earning potential</h4>
              <p><strong>We did the math.</strong></p>
              <p>
                Working closely with some of our top clients, we split their traffic using A/B tests over two months and saw Choice Pages <strong>more than doubled the earnings per click</strong> on average compared to direct-to-Amazon links.
              </p>
              <p>The biggest surprise? While our clients gained new revenue from the having more purchase options, they also <em>significantly increased commissions from their Amazon links!</em></p>
              <p><NuxtLink to="/case-study/armando-ferreira/" aria-label="Read Armando Ferreira's Choice Pages case study">Read a case study</NuxtLink> &nbsp; | &nbsp; <a href="https://geniuslink.com/blog/how-to-earn-more-affiliate-revenue-on-youtube-with-choice-pages/" aria-label="Learn more about our A/B testing results">More about our tests</a></p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic popout-topic">
            <div class="col-md-4">
              <div class="single-topic-art">
                <a target="_blank" href="https://geni.us/ldND" aria-label="View Choice Page example for electronics"><img src="/img/screen-choice-electronics.webp" class="img-responsive" width="300" height="300" alt="Turn shoppers into confident buyers"></a>
              </div>
            </div>
            <div class="col-md-6">
              <h4>Turn shoppers into confident buyers</h4>
              <p>
                "Don't extra clicks cause friction and reduce sales?" Not quite! Research shows 78% of shoppers say they like to do some comparison shopping before buying.<sup><a href="#footnotes">[1]</a></sup> Choice Pages actually reduce this friction by giving your audience a head start, and the results speak for themselves.
              </p>
              <p>
                The "Choice Effect" is about giving shoppers the choices they need, before they ask, launching them towards a successful purchase.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic">
            <div class="col-md-3 col-md-offset-1">
              <div class="single-topic-art">
                <img class="img-responsive" src="/img/feature-block-localization.svg" alt="Amazon-optimized and localized">
              </div>
            </div>
            <div class="col-md-6">
              <h4>Amazon-optimized and localized</h4>
              <p>
                Amazon links on your Choice Pages will get the same industry-leading <NuxtLink to="/features/localization">localization</NuxtLink> and affiliation we provide for all other geni.us links. We'll boost your sales by making sure that international visitors are automatically taken to the correct product in their regional Amazon store. Even better, you will earn commissions from each international associates program connected to your account.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic popout-topic longer-copy">
            <div class="col-md-4">
              <div class="single-topic-art">
                <a target="_blank" href="https://geni.us/bZVGA" aria-label="View Choice Page example for games"><img src="/img/screen-choice-game.webp" class="img-responsive" width="300" height="300" alt="Embrace shopper preferences and avoid dead-ends"></a>
              </div>
            </div>
            <div class="col-md-6">
              <h4>Embrace shopper preferences and avoid dead-ends</h4>
              <p>
                Everyone has a favorite platform where they enjoy music, books, movies, and games. Choice Pages make it easier for shoppers to buy the product that fits, and helps them avoid dead-ends like out-of-stock pages and errors.
              </p>
              <p>
                Choice Pages also help you take advantage of affiliate programs with higher payouts and longer cookie windows than Amazon. And, as your visitors comparison shop, they will set your affiliate cookies across multiple stores.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h2 class="statement"><span class="lead-in">A simpler affiliate life. <br></span>Just add links.</h2>

      <div class="row">
        <div class="col-md-7">
          <p>
            Choice Pages don't just help your audience. We created Choice Pages to save you time, simplify your life, and beautify your content.
          </p>
        </div>
      </div>
      <br>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic">
            <div class="col-md-3 col-md-offset-1">
              <div class="single-topic-art">
                <img src="/img/feature-block-stopwatch.svg" class="img-responsive" width="220" height="220" alt="Build your page in seconds with product suggestions">
              </div>
            </div>
            <div class="col-md-6">
              <h4>Build your page in seconds with product suggestions</h4>
              <p>
                Just paste your Amazon link or type a product name. We'll recommend links to that same product in other stores, and you can select the ones you want included on your page. Finally – A practical multi-retailer affiliate solution for the rest of us.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic popout-topic longer-copy">
            <div class="col-md-4">
              <div class="single-topic-art">
                <a target="_blank" href="https://geni.us/qy0M6hb" aria-label="View Choice Page example for books"><img src="/img/screen-choice-book.webp" class="img-responsive" width="300" height="300" alt="Capture commissions across multiple stores automatically"></a>
              </div>
            </div>
            <div class="col-md-6">
              <h4>Capture commissions across multiple stores automatically</h4>
              <p>
                Say goodbye to the tedious process of building special affiliate links for each retailer you work with. Once you connect your affiliate networks and programs to your Geniuslink account, just paste in a product link and we'll make sure it is perfectly formatted to earn your commissions.
              </p>
              <p>
                Geniuslink has built-in, free support for most major retailers and supports tens of thousands more if you connect your Skimlinks or Sovrn Commerce account.
              </p>
              <p><NuxtLink to="/integrations/supported/">See supported retailers and aggregators</NuxtLink></p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic">
            <div class="col-md-3  col-md-offset-1">
              <div class="single-topic-art">
                <img class="img-responsive" src="/img/feature-block-tidy-content.svg" alt="Keep your content pretty">
              </div>
            </div>
            <div class="col-md-6">
              <h4>Keep your content pretty</h4>
              <p>
                No more littering your content with multiple product URLs and "PAID LINK" disclosures. Choice Pages contain the affiliate disclosures by default, and you'll only need to share one link. Save time, look better, and give your visitors a clearer path to purchase.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic popout-topic short">
            <div class="col-md-4">
              <div class="single-topic-art">
                <a target="_blank" href="https://geni.us/QGRnuC" aria-label="View Choice Page example for luxury goods"><img src="/img/screen-choice-luxury.webp" class="img-responsive" width="300" height="300" alt="Build trust and reinforce your brand"></a>
              </div>
            </div>
            <div class="col-md-6">
              <h4>Build trust and reinforce your brand</h4>
              <p>Optionally place your own logo at the top of the page so your audience feels right at home.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic">
            <div class="col-md-3  col-md-offset-1">
              <div class="single-topic-art">
                <img class="img-responsive" src="/img/feature-block-shield.svg" alt="Compliance built-in">
              </div>
            </div>
            <div class="col-md-6">
              <h4>Compliance built-in</h4>
              <p>
                Each Choice Page includes all necessary disclaimers and disclosures required by the FTC and programs like Amazon. And because each Choice Page constitutes its own landing page, Choice Page links can be safely used where standard affiliate links would be prohibited by Amazon Associates guidelines, such as in emails and printed material.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="single-topic popout-topic">
            <div class="col-md-4">
              <div class="single-topic-art">
                <a target="_blank" href="https://geni.us/nfTmXvl" aria-label="View Choice Page example for photography gear"><img src="/img/screen-choice-gear-pics.webp" class="img-responsive" width="300" height="300" alt="Customization made simple"></a>
              </div>
            </div>
            <div class="col-md-6">
              <h4>Customization made simple</h4>
              <p>
                Let us pick the images for your product and store buttons instantly, or upload your own. Customizing is a snap with our built-in auto-sizing cropping tool, theme picker, and sortable buttons.
              </p>
              <p>Oh, and once you have found your "look", you can save all your style and logo settings as your new defaults for next time. Bada-boom.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="big-quote">
          <img src="/img/quote-feliciotti.webp" alt="Andy Feliciotti">
          <p>"I've noticed customers want to be able to choose who they buy from, making Geniuslink's new Choice Pages highly lucrative. Choice Pages have easily increased my conversion rates and helped me diversify my income away from one source."</p>
          <p class="big-quote-by">Andy Feliciotti - Founder, <a href="https://awesomestufftobuy.com" target="_blank" rel="noopener noreferrer">awesomestufftobuy.com</a></p>
        </div>
      </div>

      <ul class="footnotes" id="footnotes">
        <li>
          <sup>1</sup>AYTM Market Research, 'Comparison Shopping Survey: Consumers Turn to Big Retailers for Info', <em>AYTM.com</em>, 2016, <a href="https://aytm.com/blog/comparison-shopping-survey/">https://aytm.com/blog/comparison-shopping-survey/</a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style>
body.section-choicepages header.affix #nav { background: #736379; }
</style>
